import { memo } from "react";
import { useDispatch, useSelector } from "react-redux";
import Spinner from "../common/components/Spinner/Spinner";
import { useOnMount } from "../common/hooks/useOnMount";
import { type RootState } from "../redux/store";
import { EmptyCartPage } from "~/feature/cart/cart/components/CartPage/EmptyCartPage";
import { B2BCartPage } from "~/pages/B2BCartPage";
import { CartPageContextProvider } from "~/feature/cart/cart/CartPageContext";
import { cartPageLoadThunk } from "~/feature/cart/cart/redux/cartPageLoadThunk";
import { userIsCustomer } from "~/utils/roles";
import { B2CCartPage } from "~/pages/B2CCartPage";
import { FCCGapProductRecommendation } from "../feature/SaleRecommendations/FCCRecommendationsSection/SchaefflerFCCGapProductsRecommendationComponent";
import { PurchasePlaceProvider } from "~/feature/Analytics/ga/recommendations/RecommendationContexts";
import { AlternateProductsProvider, useAlternateProducts } from "~/feature/cart/cart/AlternateProductsContext";

export const CartPage = memo(() => {
  const dispatch = useDispatch();
  useOnMount(() => {
    dispatch(cartPageLoadThunk());
  });
  const { alternateItems } = useAlternateProducts();

  const isCartLoading = useSelector(selectIsCartLoading);
  const isCartEmpty = useSelector(selectIsCartEmpty);

  if (isCartLoading) {
    return <Spinner />;
  }

  if (isCartEmpty && alternateItems.length == 0) {
    return (
      <>
        <EmptyCartPage />
        <FCCGapProductRecommendation />
      </>
    );
  }

  if (!userIsCustomer()) {
    return <B2CCartPage />;
  }

  return <B2BCartPage />;
});

const selectIsCartLoading = (state: RootState) => state.cartPage.loading;
const selectIsCartEmpty = (state: RootState) => selectIsCartLoading(state) || state.cart.cartEntries.length === 0;

export interface Props {
  disableCheckout: boolean;
  showSubtotal: boolean;
}

export function createCartPage(props: Props) {
  return (
    <PurchasePlaceProvider value={"CART"}>
      <CartPageContextProvider initialDisableCheckout={props.disableCheckout} initialShowSubtotal={props.showSubtotal}>
        <AlternateProductsProvider>
          <CartPage />
        </AlternateProductsProvider>
      </CartPageContextProvider>
    </PurchasePlaceProvider>
  );
}
